import React, { useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal, Popover } from '@brighterion/br-ui';
import useCreateGlobalBulkUploadErrorCSV from 'Hooks/query-hooks/useCreateGlobalRulesBulkUploadErrorCSV';
import useCreateMerchantBulkUploadErrorCSV from 'Hooks/query-hooks/useCreateMerchantOverridesBulkUploadErrorCSV';
import { format } from 'date-fns';
import { replaceAllMerchantLabels, handleOnClickEvent, handleKeyEvent } from 'Utils';
import { RuleType } from 'Common/enums';
import { Column, Row } from 'react-table';
import styles from './index.css';
import Table from '../Table';

type Props = {
    result: UploadErrorsType;
    onClose: () => void;
    uploadedFile: File;
    isOpen: boolean;
    parsedFileTableData: ParsedFileTableData;
    errorContentRenderer: (result: UploadErrorsType) => JSX.Element;
    rulesType: RulesType;
};

export default function Form({
    result,
    onClose,
    uploadedFile,
    isOpen,
    parsedFileTableData,
    errorContentRenderer,
    rulesType
}: Props) {
    const { t } = useTranslation();
    const fileName = uploadedFile?.name.substring(0, uploadedFile?.name.lastIndexOf('.'));
    const today = format(new Date(), 'MMddyyyy');
    const errorCsvName = `${fileName}_bulk-upload_file_${today}_errors_${result?.errors.length}`;
    const formattedErrors = result?.errors.map((err: UploadErrorsType) => {
        return {
            ...err,
            errors: err.errors?.map((errMsgs: string) => replaceAllMerchantLabels(errMsgs, t))
        };
    });
    const globalRulesCSV = useCreateGlobalBulkUploadErrorCSV(formattedErrors, errorCsvName);
    const merchantOverridesCSV = useCreateMerchantBulkUploadErrorCSV(formattedErrors, errorCsvName);
    const errorCols = useMemo<Column<any>[]>(() => {
        return parsedFileTableData
            ? [
                  {
                      accessor: 'row',
                      Header: () => t('Row')
                  },
                  {
                      accessor: 'errors',
                      Header: () => t('Errors'),
                      renderType: 'overrideCell',
                      Cell: ({ value, row }: { value: string; row: Row }) => {
                          const errors = value.split('@#$');
                          return (
                              <Popover
                                  isAltText
                                  content={
                                      <>
                                          {errors.map((val: string, i: number) => (
                                              <div
                                                  key={`err-popover-${row.id}-${i * 2}`}
                                                  className={styles.errPopoverEntry}
                                              >
                                                  {val}
                                              </div>
                                          ))}
                                      </>
                                  }
                                  contentClassName={styles.altTextContent}
                                  popperClassName={styles.popper}
                              >
                                  <span className={styles.errCol} id={`err-${row.id}`}>
                                      {errors?.length}
                                  </span>
                              </Popover>
                          );
                      }
                  },
                  ...parsedFileTableData.columns
              ]
            : [];
    }, [parsedFileTableData, t]);

    const errorData = useMemo<any[]>(() => {
        if (_.isArray(formattedErrors)) {
            return formattedErrors.map((i: any) => ({
                row: i.rowNumber,
                errors: i.errors.join('@#$'),
                'rule id': i.rule?.ruleId ?? i.rule?.id,
                identifier: i.rule?.merchantId,
                'score value': i.rule?.score,
                ...i.rule
            }));
        }
        return [];
    }, [formattedErrors]);

    const modalSetup = useMemo(() => {
        return {
            className: styles.errorModal,
            header: (
                <span className={styles.errorModalHeader}>
                    <Icon name='error' size='xlg' /> {t('Errors found')}
                </span>
            ),
            columns: errorCols,
            tableData: errorData
        };
    }, [t, errorCols, errorData]);

    const renderFileIcon = () => {
        if (uploadedFile.type === '') {
            const extension = uploadedFile.name.split('.')[1];
            return (
                <Icon
                    data-test-class='uploaded-file-icon'
                    className={styles.uploadedFileIcon}
                    name={`${extension}-file`}
                    size='lg'
                />
            );
        }
        let iconName;
        switch (uploadedFile.type) {
            case 'text/plain': {
                iconName = 'txt-file';
                break;
            }
            case 'application/pdf': {
                iconName = 'pdf-file';
                break;
            }
            case 'csv/plain': {
                iconName = 'csv-file';
                break;
            }
            case 'application/xls': {
                iconName = 'xls-file';
                break;
            }
            default:
                iconName = '';
        }
        if (iconName) {
            return (
                <Icon
                    className={styles.uploadedFileIcon}
                    data-test-class='uploaded-file-icon'
                    name={iconName}
                    size='lg'
                />
            );
        }
        return null;
    };

    const getReadableBytes = () => {
        if (uploadedFile.size === 0) {
            return '0 B';
        }
        const i = Math.floor(Math.log(uploadedFile.size) / Math.log(1024));
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        return `${(uploadedFile.size / 1024 ** i).toFixed(2)} ${sizes[i]}`;
    };

    const exportCSV = () => {
        if (rulesType === RuleType.GlobalRules) globalRulesCSV.mutate();
        if (rulesType === RuleType.MerchantOverrides) merchantOverridesCSV.mutate();
    };

    return (
        <Modal isOpen={isOpen} closeModal={onClose} status='error' closeOnClickOutside={false}>
            <Modal.Title
                id='bulk-upload-error-modal-title'
                title={t('Errors found in file')}
                statusIcon='error'
                onClose={onClose}
            >
                <span className={styles.errorTitleContent}>
                    <>{uploadedFile && renderFileIcon()}</>
                    <span className={styles.errModalFileName} data-testid='uploaded-file-name'>
                        {uploadedFile?.name}
                    </span>
                    <span data-test-class='uploaded-file-size' className={styles.fileSize}>
                        {uploadedFile && getReadableBytes()}
                    </span>
                </span>
            </Modal.Title>
            {errorContentRenderer(result)}
            <div className={styles.tableOverflowContainer}>
                <Table
                    tableName='error-modal-table'
                    dataTestId='error-modal-table'
                    columns={modalSetup.columns}
                    data={modalSetup.tableData}
                    initialState={{
                        sortBy: [{ id: 'row', desc: false }]
                    }}
                />
            </div>
            <div className={styles.buttonsRow}>
                <Button
                    variant='secondary'
                    data-test-id='export-errors-csv-btn'
                    onClick={handleOnClickEvent(exportCSV)}
                    onKeyDown={handleKeyEvent(exportCSV)}
                >
                    {t('Export CSV')}
                </Button>
                <Button
                    className={styles.cancelButton}
                    variant='primary'
                    size='medium'
                    data-test-id='close-btn'
                    onClick={handleOnClickEvent(onClose)}
                    onKeyDown={handleKeyEvent(onClose)}
                >
                    {t('Close')}
                </Button>
            </div>
        </Modal>
    );
}
